import React, { useState, useRef } from 'react';
import { Upload, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter
} from "../../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
    SelectSeparator,
} from "../../../components/ui/select";
import { Asset } from '../../../types/asset';
import api from '../../../utils/api';
import { toast } from 'sonner';

interface AssetDocumentModalProps {
    open: boolean;
    onClose: () => void;
    asset: Asset;
}

const documentGroups = [
    {
        group: "Purchase & Ownership",
        options: [
            "Purchase Invoice",
            "Purchase Contract",
            "Delivery Docket",
            "Proof of Ownership",
            "Asset Photos (Purchase)",
            "Contract/Agreement"
        ]
    },
    {
        group: "Warranty",
        options: [
            "Warranty Agreement",
            "Warranty Certificate",
            "Warranty Claim"
        ]
    },
    {
        group: "Service Agreements",
        options: [
            "Service Agreement",
            "Maintenance Contract",
            "SLA"
        ]
    },
    {
        group: "Vehicle Documents",
        options: [
            "Registration Papers",
            "CTP / Greenslip",
            "Comprehensive Insurance",
            "Roadworthy Certificate"
        ]
    },
    {
        group: "Compliance & Certificates",
        options: [
            "Compliance Certificate"
        ]
    }
];

const AssetDocumentModal: React.FC<AssetDocumentModalProps> = ({ open, onClose, asset }) => {
    const [title, setTitle] = useState('');
    const [type, setType] = useState('');
    const [issuer, setIssuer] = useState('');
    const [issueDate, setIssueDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [notes, setNotes] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            setFile(e.target.files[0]);
        }
    };

    const handleSubmit = async () => {
        if (!title || !type || !file) {
            toast.error("Please fill all required fields and select a file.", { position: 'top-center' });
            return;
        }

        try {
            setIsUploading(true);
            const formData = new FormData();
            formData.append('title', title);
            formData.append('type', type);
            if (issuer) formData.append('issuer', issuer);
            if (issueDate) formData.append('issueDate', issueDate);
            if (expiryDate) formData.append('expiryDate', expiryDate);
            if (notes) formData.append('notes', notes);
            formData.append('file', file);

            await api.post(`/api/assets/${asset.id}/documents`, formData);

            toast.success("Document uploaded successfully!", { position: 'top-center' });

            // clear state
            setTitle(''); setType(''); setIssuer(''); setIssueDate(''); setExpiryDate(''); setNotes(''); setFile(null);
            onClose();
        } catch (error: any) {
            toast.error(error.message || "Failed to upload document", { position: 'top-center' });
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onClose}>
            <DialogContent className="max-w-xl rounded-2xl p-0 gap-0 overflow-hidden bg-white border-0 shadow-2xl">
                <DialogHeader className="p-6 pb-4">
                    <div className="flex items-center gap-3 mb-2">
                        <Upload className="text-gray-900" size={24} />
                        <DialogTitle className="text-xl font-bold">Upload Document</DialogTitle>
                    </div>
                    <DialogDescription className="text-sm font-medium text-gray-500">
                        Upload a new document for {asset.name} - {asset.tag}
                    </DialogDescription>
                </DialogHeader>

                <div className="px-6 py-4 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {/* Document Title */}
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            Document Title <span className="text-red-500">*</span>
                        </label>
                        <Input
                            placeholder="e.g., 2026 Vehicle Registration"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="h-11 rounded-xl bg-gray-50/50 border-gray-100 placeholder:text-gray-400 font-medium font-sm"
                        />
                    </div>

                    {/* Document Type */}
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            Document Type <span className="text-red-500">*</span>
                        </label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="w-full h-11 rounded-xl bg-white border-gray-200 text-sm font-medium">
                                <SelectValue placeholder="Select document type" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl border border-gray-100 shadow-xl max-h-[300px]">
                                {documentGroups.map((group, index) => (
                                    <React.Fragment key={group.group}>
                                        <SelectGroup>
                                            <SelectLabel className="font-bold text-gray-900 text-xs uppercase tracking-wider px-2 py-2">
                                                {group.group}
                                            </SelectLabel>
                                            {group.options.map((option) => (
                                                <SelectItem
                                                    key={option}
                                                    value={option}
                                                    className="font-medium text-sm text-gray-700 focus:bg-gray-50 cursor-pointer rounded-lg mx-1 my-0.5"
                                                >
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectGroup>
                                        {index < documentGroups.length - 1 && <SelectSeparator className="bg-gray-50" />}
                                    </React.Fragment>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Issuer / Provider */}
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            Issuer / Provider
                        </label>
                        <Input
                            placeholder="e.g., NRMA Insurance, RMS, Vendor Name"
                            value={issuer}
                            onChange={(e) => setIssuer(e.target.value)}
                            className="h-11 rounded-xl bg-gray-50/50 border-gray-100 placeholder:text-gray-400 font-medium font-sm"
                        />
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                                Issue Date
                            </label>
                            <Input
                                type="date"
                                value={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                                className="h-11 rounded-xl bg-gray-50/50 border-gray-100 font-medium font-sm text-gray-600 w-full"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                                Expiry Date
                            </label>
                            <Input
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                className="h-11 rounded-xl bg-gray-50/50 border-gray-100 font-medium font-sm text-gray-600 w-full"
                            />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            Notes
                        </label>
                        <Textarea
                            placeholder="Additional notes or comments..."
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="min-h-[100px] rounded-xl bg-gray-50/50 border-gray-100 placeholder:text-gray-400 font-medium font-sm resize-none custom-scrollbar p-3"
                        />
                    </div>

                    {/* File Upload Box */}
                    <div className="space-y-2 pb-2">
                        <label className="text-sm font-bold text-gray-900 flex items-center gap-1">
                            Files <span className="text-red-500">*</span>
                        </label>
                        <div
                            className="border-2 border-dashed border-gray-200 rounded-2xl p-8 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-gray-50/50 hover:border-gray-300 transition-colors bg-white"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4">
                                <Upload className="text-gray-500" size={24} />
                            </div>
                            <h4 className="font-bold text-gray-900 text-sm mb-1">
                                {file ? file.name : "Click to upload or drag and drop"}
                            </h4>
                            <p className="text-xs font-medium text-gray-400">
                                {file ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "PDF, JPG, PNG (max 10MB each)"}
                            </p>
                            <input
                                type="file"
                                className="hidden"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".pdf,.jpg,.jpeg,.png"
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter className="p-6 bg-white border-t border-gray-50 flex sm:justify-end gap-3 mt-auto">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="rounded-xl border-gray-200 font-bold h-11 w-full sm:w-auto"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isUploading}
                        className="rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold h-11 w-full sm:w-auto gap-2"
                    >
                        {isUploading ? (
                            <div className="w-4 h-4 rounded-full border-2 border-white/20 border-t-white animate-spin" />
                        ) : (
                            <Upload size={16} />
                        )}
                        {isUploading ? "Uploading..." : "Upload Document"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default AssetDocumentModal;
